import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogAPI } from '../services/api';
import { Calendar, Eye, Tag, ArrowLeft, User, Clock } from 'lucide-react';
import { formatDateDisplay } from '../lib/dateUtils';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

import { FlutterFoxFooter } from '../components/FlutterFoxFooter';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    blogAPI.getPublic(slug)
      .then(r => {
        setPost(r.data);
        // Update page title for SEO
        document.title = `${r.data.meta_title || r.data.title} | Transsolution Blog`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', r.data.meta_description || r.data.excerpt);
      })
      .catch(() => navigate('/blog'))
      .finally(() => setLoading(false));
    return () => { document.title = 'Transsolution ERP'; };
  }, [slug, navigate]);

  if (loading) return (
    <div className="bg-slate-950 min-h-screen flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!post) return null;

  const htmlContent = DOMPurify.sanitize(marked.parse(post.content || ''));

  return (
    <div className="bg-slate-950 text-white min-h-screen" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Nav */}
      <nav className="bg-slate-950/95 backdrop-blur-md border-b border-slate-800/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-3">
            <img src="/images/transsolution_logo.png" alt="Transsolution" className="w-8 h-8 rounded object-contain bg-white p-0.5" />
            <span className="font-bold text-white text-sm tracking-tight" style={{ fontFamily: 'Barlow, sans-serif' }}>TRANSSOLUTION</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/blog" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">Blog</a>
            <a href="/contact" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">Contact</a>
            <button onClick={() => navigate('/login')} className="text-sm text-slate-300 hover:text-white px-4 py-2">Login</button>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="py-12 md:py-20" data-testid="blog-article">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Back */}
          <a href="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-orange-500 mb-8 transition-colors" data-testid="back-to-blog">
            <ArrowLeft size={14} /> Back to Blog
          </a>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((t, i) => (
                <span key={i} className="text-xs font-semibold px-3 py-1 rounded-full bg-orange-600/10 text-orange-400">{t}</span>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-6" style={{ fontFamily: 'Barlow, sans-serif' }} data-testid="blog-post-title">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-8 pb-8 border-b border-slate-800">
            <span className="flex items-center gap-1.5"><User size={14} /> {post.author_name}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDateDisplay(post.published_at || post.created_at)}</span>
            {post.views > 0 && <span className="flex items-center gap-1.5"><Eye size={14} /> {post.views} views</span>}
          </div>

          {/* Cover Image */}
          {post.cover_image && (
            <div className="rounded-xl overflow-hidden mb-10">
              <img src={post.cover_image} alt={post.title} className="w-full h-auto" />
            </div>
          )}

          {/* Content */}
          <div
            className="prose prose-invert prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-2xl prose-h3:text-xl prose-p:text-slate-300 prose-p:leading-relaxed prose-a:text-orange-400 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-100 prose-li:text-slate-300 prose-code:bg-slate-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 prose-pre:border prose-pre:border-slate-800"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
            data-testid="blog-content"
          />

          {/* CTA */}
          <div className="mt-12 p-6 bg-gradient-to-br from-orange-600/20 to-slate-900 border border-orange-500/20 rounded-xl text-center">
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Barlow, sans-serif' }}>Ready to streamline your logistics?</h3>
            <p className="text-sm text-slate-400 mb-4">Try Transsolution ERP free — no credit card required.</p>
            <a href="/login" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold px-6 py-3 rounded-lg tracking-wide transition-all text-sm">
              Start Free Trial
            </a>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-wrap items-center justify-between gap-4">
          <span className="text-xs text-slate-500">&copy; 2026 Transsolution ERP.</span>
          <FlutterFoxFooter testId="blog-post-footer" />
        </div>
      </footer>
    </div>
  );
}
